import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import styles from './LegendManager.module.css';
import Button from '../Button/Button';
import {
    PlusIcon,
    TrashIcon,
    ImportIcon,
    BoundaryIcon,
    BuildingIcon,
    RoadIcon,
    PinIcon,
    CloseIcon,
    XCircleIcon
} from '../Icon';
import { getHatchBackground, COLOR_PALETTES, HATCH_OPTIONS, LINE_TYPE_OPTIONS } from '../../utils/legendStyles';

/**
 * Интерактивный компактный ColorPicker порталом поверх всех блоков
 */
const ColorPickerPopover = ({ color, hatch, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [customHex, setCustomHex] = useState(color || '#3b82f6');
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const popoverRef = useRef(null);

    useEffect(() => {
        setCustomHex(color || '#3b82f6');
    }, [color]);

    const handleToggle = () => {
        if (!isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const popoverHeight = 290;
            const popoverWidth = 250;
            
            // Если снизу мало места — открываем над кнопкой
            let top = rect.bottom + 6;
            if (window.innerHeight - rect.bottom < popoverHeight && rect.top > popoverHeight) {
                top = rect.top - popoverHeight - 6;
            }
            let left = rect.left;
            if (left + popoverWidth > window.innerWidth - 10) {
                left = window.innerWidth - popoverWidth - 10;
            }
            setCoords({ top, left });
        }
        setIsOpen(!isOpen);
    };

    // Закрытие при клике снаружи или прокрутке родительских блоков
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                popoverRef.current && 
                !popoverRef.current.contains(e.target) &&
                triggerRef.current &&
                !triggerRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };
        const handleScroll = (e) => {
            if (isOpen && popoverRef.current && !popoverRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('scroll', handleScroll, true);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isOpen]);

    const handleHexSubmit = (e) => {
        e.preventDefault();
        if (/^#[0-9A-F]{6}$/i.test(customHex) || /^#[0-9A-F]{3}$/i.test(customHex) || customHex === 'transparent') {
            onChange(customHex);
            setIsOpen(false);
        } else {
            toast.warn('Введите корректный HEX цвет, например #22c55e');
        }
    };

    const popoverElement = isOpen ? (
        <div
            className={styles.colorPopover}
            ref={popoverRef}
            style={{
                top: `${coords.top}px`,
                left: `${coords.left}px`
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <div className={styles.popoverHeader}>
                <span>Палитра цветов</span>
                <button type="button" className={styles.popoverCloseBtn} onClick={() => setIsOpen(false)} title="Закрыть">
                    <CloseIcon size={13} />
                </button>
            </div>

            <div className={styles.palettesList}>
                {COLOR_PALETTES.map(palette => (
                    <div key={palette.name} className={styles.paletteGroup}>
                        <div className={styles.paletteTitle}>{palette.name}</div>
                        <div className={styles.paletteSwatches}>
                            {palette.colors.map(c => (
                                <button
                                    key={c.color}
                                    type="button"
                                    className={`${styles.paletteCircle} ${color === c.color ? styles.paletteCircleActive : ''}`}
                                    style={{ backgroundColor: c.color }}
                                    onClick={() => {
                                        onChange(c.color);
                                        setCustomHex(c.color);
                                    }}
                                    title={`${c.name} (${c.color})`}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className={styles.customColorSection}>
                <div className={styles.customColorRow}>
                    <label className={styles.nativeColorLabel} title="Открыть системную палитру">
                        <input
                            type="color"
                            value={color && color !== 'transparent' ? color : '#3b82f6'}
                            className={styles.nativeColorInput}
                            onChange={(e) => {
                                setCustomHex(e.target.value);
                                onChange(e.target.value);
                            }}
                        />
                        <span className={styles.nativeColorTrigger} style={{ backgroundColor: color && color !== 'transparent' ? color : '#3b82f6' }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ffffff' }}>
                                <circle cx="12" cy="12" r="10" />
                                <path d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z" />
                            </svg>
                        </span>
                    </label>

                    <input
                        type="text"
                        value={customHex}
                        onChange={(e) => setCustomHex(e.target.value)}
                        className={styles.hexInput}
                        placeholder="#HEX"
                        maxLength={7}
                    />

                    <button
                        type="button"
                        className={styles.applyHexBtn}
                        onClick={handleHexSubmit}
                    >
                        OK
                    </button>
                </div>
            </div>

            <div className={styles.popoverFooter}>
                <button
                    type="button"
                    className={styles.transparentBtn}
                    onClick={() => {
                        onChange('transparent');
                        setIsOpen(false);
                    }}
                >
                    <XCircleIcon size={12} /> Без заливки (контур)
                </button>
            </div>
        </div>
    ) : null;

    return (
        <div className={styles.colorPickerContainer}>
            <button
                ref={triggerRef}
                type="button"
                className={styles.colorSwatchTrigger}
                onClick={handleToggle}
                title="Выбрать цвет и палитру"
            >
                <span
                    className={styles.colorSwatchPreview}
                    style={{
                        backgroundColor: color === 'transparent' ? 'transparent' : color,
                        backgroundImage: color === 'transparent' ? 'repeating-conic-gradient(#cbd5e1 0% 25%, transparent 0% 50%) 50% / 8px 8px' : getHatchBackground(hatch)
                    }}
                />
                <span className={styles.colorHexLabel}>{color === 'transparent' ? 'Прозрачный' : color}</span>
            </button>

            {isOpen && ReactDOM.createPortal(popoverElement, document.body)}
        </div>
    );
};

const LegendManager = ({ isVisible, onClose, onSave }) => {
    const [legendData, setLegendData] = useState({ plots: [], buildings: [], roads: [], markers: [] });
    const [activeTab, setActiveTab] = useState('plots');
    const [deleteTarget, setDeleteTarget] = useState(null); // { index, name }
    const fileInputRef = useRef(null);
    const [uploadIndex, setUploadIndex] = useState(null);

    useEffect(() => {
        if (isVisible) {
            fetch('/api/legend')
                .then(res => res.json())
                .then(data => {
                    const processedData = { ...data };
                    Object.keys(processedData).forEach(type => {
                        processedData[type] = (processedData[type] || []).map(item => {
                            if (item.lineType === undefined) {
                                item.lineType = type === 'roads' ? 'solid' : null;
                            }
                            if (item.hatch === undefined) {
                                item.hatch = (type === 'plots' || type === 'buildings') ? 'none' : null;
                            }
                            if (item.image_filename === undefined) {
                                item.image_filename = type === 'markers' ? '' : null;
                            }
                            return item;
                        });
                    });
                    setLegendData(processedData);
                })
                .catch(() => toast.error('Не удалось загрузить легенду'));
        }
    }, [isVisible]);

    const handleAddItem = () => {
        const newItem = {
            id: `${activeTab}_${Date.now()}`,
            name: 'Новая категория',
            color: activeTab === 'roads' ? '#3b82f6' : activeTab === 'buildings' ? '#475569' : '#22c55e',
            lineType: activeTab === 'roads' ? 'solid' : null,
            hatch: (activeTab === 'plots' || activeTab === 'buildings') ? 'none' : null,
            image_filename: activeTab === 'markers' ? null : undefined,
            image_url: activeTab === 'markers' ? null : undefined,
        };
        setLegendData(prev => ({
            ...prev,
            [activeTab]: [...prev[activeTab], newItem]
        }));
    };

    const handleItemChange = (index, field, value) => {
        const items = [...legendData[activeTab]];
        items[index] = { ...items[index], [field]: value };
        setLegendData(prev => ({ ...prev, [activeTab]: items }));
    };

    const handleDeleteClick = (index, item) => {
        setDeleteTarget({
            index,
            name: item.name?.trim() || 'Без названия'
        });
    };

    const handleConfirmDelete = () => {
        if (!deleteTarget) return;
        const { index } = deleteTarget;
        const currentItems = [...legendData[activeTab]];
        const itemToRemove = currentItems[index];
        const updatedItems = currentItems.filter((_, i) => i !== index);
        setLegendData(prev => ({ ...prev, [activeTab]: updatedItems }));

        if (activeTab === 'markers' && itemToRemove.image_filename) {
            fetch('/api/delete-image', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ filename: itemToRemove.image_filename })
            }).then(res => {
                if (res.ok) {
                    toast.info('Файл изображения удален с сервера.');
                }
            }).catch(() => {});
        }

        setDeleteTarget(null);
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file || uploadIndex === null) return;

        const oldFilename = legendData[activeTab][uploadIndex]?.image_filename;
        const formData = new FormData();
        formData.append('image', file);

        fetch('/api/upload/image', {
            method: 'POST',
            body: formData,
        })
            .then(res => {
                if (!res.ok) throw new Error('Ошибка загрузки изображения');
                return res.json();
            })
            .then(data => {
                handleItemChange(uploadIndex, 'image_url', data.url);
                handleItemChange(uploadIndex, 'image_filename', data.filename);
                toast.success('Изображение успешно загружено');

                if (oldFilename && oldFilename !== data.filename) {
                    fetch('/api/delete-image', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ filename: oldFilename })
                    });
                }
            })
            .catch(err => {
                toast.error(err.message || 'Не удалось загрузить изображение');
            })
            .finally(() => {
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                setUploadIndex(null);
            });
    };

    const handleSaveChanges = () => {
        fetch('/api/legend', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(legendData)
        })
            .then(res => {
                if (!res.ok) throw new Error('Server error');
                toast.success('Легенда успешно сохранена!');
                onSave();
                onClose();
            })
            .catch(() => toast.error('Ошибка при сохранении легенды'));
    };

    if (!isVisible) return null;

    const tabs = [
        { id: 'plots', name: 'Участки', Icon: BoundaryIcon, count: legendData.plots?.length || 0 },
        { id: 'buildings', name: 'Здания', Icon: BuildingIcon, count: legendData.buildings?.length || 0 },
        { id: 'roads', name: 'Линии', Icon: RoadIcon, count: legendData.roads?.length || 0 },
        { id: 'markers', name: 'Метки', Icon: PinIcon, count: legendData.markers?.length || 0 },
    ];

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                {/* Шапка модального окна */}
                <div className={styles.header}>
                    <div className={styles.headerTitleGroup}>
                        <h2>Менеджер легенды</h2>
                        <span className={styles.headerSubtitle}>Настройка цветов, штриховки и категорий карты</span>
                    </div>
                    <button onClick={onClose} className={styles.closeButton} title="Закрыть">
                        <CloseIcon size={18} />
                    </button>
                </div>

                {/* Вкладки категорий */}
                <div className={styles.tabs}>
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            type="button"
                            className={`${styles.tabButton} ${activeTab === tab.id ? styles.active : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <tab.Icon size={16} className={styles.tabIcon} />
                            <span>{tab.name}</span>
                            <span className={styles.tabCount}>{tab.count}</span>
                        </button>
                    ))}
                </div>

                {/* Контент списка категорий */}
                <div className={styles.content}>
                    <div className={styles.listHeader}>
                        <span>{activeTab === 'markers' ? 'Иконка' : 'Цвет и стиль'}</span>
                        <span>Название категории</span>
                        <span>
                            {activeTab === 'roads' ? 'Тип линии' :
                                activeTab === 'plots' || activeTab === 'buildings' ? 'Штриховка' :
                                    activeTab === 'markers' ? 'Файл иконки' : ''}
                        </span>
                        <span className={styles.actionsHeader}>Удалить</span>
                    </div>

                    <div className={styles.listScrollArea}>
                        <div className={styles.itemsList}>
                            {legendData[activeTab]?.length === 0 ? (
                                <div className={styles.emptyState}>
                                    <span>В этой секции пока нет категорий.</span>
                                </div>
                            ) : (
                                legendData[activeTab]?.map((item, index) => (
                                    <div key={item.id} className={styles.listItem}>
                                        {/* Колонка 1: Цвет / Иконка */}
                                        {activeTab === 'markers' ? (
                                            <div className={styles.imagePreview}>
                                                {item.image_url ? (
                                                    <img src={item.image_url} alt={item.name} />
                                                ) : (
                                                    <PinIcon size={16} className={styles.markerFallback} />
                                                )}
                                            </div>
                                        ) : (
                                            <ColorPickerPopover
                                                color={item.color}
                                                hatch={item.hatch}
                                                onChange={(newColor) => handleItemChange(index, 'color', newColor)}
                                            />
                                        )}

                                        {/* Колонка 2: Название */}
                                        <input
                                            type="text"
                                            value={item.name}
                                            className={styles.nameInput}
                                            placeholder="Название категории"
                                            onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                                        />

                                        {/* Колонка 3: Штриховка / Тип линии / Загрузка */}
                                        <div className={styles.styleOptionsCell}>
                                            {activeTab === 'roads' ? (
                                                <div className={styles.selectWrapper}>
                                                    <select
                                                        value={item.lineType || 'solid'}
                                                        className={styles.styleSelect}
                                                        onChange={(e) => handleItemChange(index, 'lineType', e.target.value)}
                                                    >
                                                        {LINE_TYPE_OPTIONS.map(opt => (
                                                            <option key={opt.id} value={opt.id}>
                                                                {opt.pattern} {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ) : activeTab === 'plots' || activeTab === 'buildings' ? (
                                                <div className={styles.hatchCell}>
                                                    <select
                                                        value={item.hatch || 'none'}
                                                        className={styles.styleSelect}
                                                        onChange={(e) => handleItemChange(index, 'hatch', e.target.value)}
                                                    >
                                                        {HATCH_OPTIONS.map(opt => (
                                                            <option key={opt.id} value={opt.id}>
                                                                {opt.icon} {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ) : activeTab === 'markers' ? (
                                                <Button
                                                    variant="secondary"
                                                    onClick={() => {
                                                        setUploadIndex(index);
                                                        fileInputRef.current?.click();
                                                    }}
                                                    title="Загрузить новое изображение"
                                                    className={styles.uploadBtn}
                                                >
                                                    <ImportIcon size={14} /> Загрузить
                                                </Button>
                                            ) : null}
                                        </div>

                                        {/* Колонка 4: Удаление */}
                                        <div className={styles.actionsCell}>
                                            <button
                                                type="button"
                                                className={styles.deleteBtn}
                                                onClick={() => handleDeleteClick(index, item)}
                                                title="Удалить категорию"
                                            >
                                                <TrashIcon size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Кнопка добавления категории всегда внизу списка */}
                        <button type="button" onClick={handleAddItem} className={styles.addCategoryBtn}>
                            <PlusIcon size={16} /> Добавить категорию
                        </button>
                    </div>

                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        accept="image/png, image/jpeg, image/svg+xml"
                        onChange={handleImageUpload}
                    />
                </div>

                {/* Футер */}
                <div className={styles.footer}>
                    <Button variant="secondary" onClick={onClose}>Отмена</Button>
                    <button type="button" onClick={handleSaveChanges} className={styles.saveButton}>
                        Сохранить изменения
                    </button>
                </div>
            </div>

            {/* Кастомное модальное окно подтверждения удаления */}
            {deleteTarget && (
                <div className={styles.confirmOverlay} onClick={() => setDeleteTarget(null)}>
                    <div className={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.confirmIconWrapper}>
                            <TrashIcon size={24} />
                        </div>
                        <h3 className={styles.confirmTitle}>Удалить категорию?</h3>
                        <p className={styles.confirmMessage}>
                            Вы действительно хотите удалить категорию <strong>«{deleteTarget.name}»</strong>?
                        </p>
                        <div className={styles.confirmActions}>
                            <button
                                type="button"
                                className={styles.confirmCancelBtn}
                                onClick={() => setDeleteTarget(null)}
                            >
                                Отмена
                            </button>
                            <button
                                type="button"
                                className={styles.confirmDeleteBtn}
                                onClick={handleConfirmDelete}
                            >
                                Удалить
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LegendManager;